import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import {EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID, EmotoradEventTypes, EmotoradLeadEventStatusEnum} from "~/domainModels/workspaceSpecific/emotorad";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getEventsWithEventContainers, updateEvent} from "~/utilities/apis/api";
import {uniqueUuid} from "@vesta/unique/unique";

/** Aligns with Event Container profile event-list filters. */
const EVENTS_WITH_EVENT_CONTAINER_FILTER_REVISION_ID = uniqueUuid`11beb352-d956-4e82-ae53-658ed0b2bb03`;
/** Aligns with the Event Container profile events-tab default sort. */
const EVENT_CREATED_AT_ASC_SORT_REVISION_ID = uniqueUuid`b88695bd-591c-4d1f-a32d-7f25fae5245c`;

/**
 * For an existing Event Container, loads the earliest Lead (enquiry) event by `createdAt`.
 * If its lead row status is still {@link EmotoradLeadEventStatusEnum.OPEN}, updates it to
 * {@link EmotoradLeadEventStatusEnum.ENGAGED}. Otherwise no-op.
 */
export async function updateInitialLead(params: {accessToken: string; eventContainerId: Uuid; intellsysBaseUrl: string; workspaceId: Uuid}): Promise<Result<void>> {
    const leadTemplateId = EmotoradEventTypes.lead as Uuid;
    const eventsResult = await getEventsWithEventContainers({
        accessToken: params.accessToken,
        intellsysBaseUrl: params.intellsysBaseUrl,
        workspaceId: params.workspaceId,
        limit: 1,
        offset: 0,
        filterConfiguration: {
            revisionId: EVENTS_WITH_EVENT_CONTAINER_FILTER_REVISION_ID,
            lineItems: [
                {
                    columnId: "templateId",
                    operation: FilterOperation.equals,
                    value: leadTemplateId,
                },
                {
                    columnId: "eventContainer.id",
                    operation: FilterOperation.equals,
                    value: params.eventContainerId,
                },
            ],
        },
        sortConfiguration: {
            revisionId: EVENT_CREATED_AT_ASC_SORT_REVISION_ID,
            lineItems: [{columnId: "createdAt", direction: SortDirection.ascending}],
        },
    });
    if (eventsResult.success !== true) {
        return eventsResult;
    }
    const firstLead = eventsResult.data[0];
    if (firstLead === undefined) {
        console.error("No lead event found for Event Container", params.eventContainerId);
        return dataResult(undefined);
    }
    const statusRaw = firstLead.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID];
    if (String(statusRaw) !== String(EmotoradLeadEventStatusEnum.OPEN)) {
        return dataResult(undefined);
    }
    const mergedAdditional: Record<Uuid, unknown> = {...firstLead.additionalColumnValues};
    mergedAdditional[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID] = EmotoradLeadEventStatusEnum.ENGAGED;
    return updateEvent({
        accessToken: params.accessToken,
        intellsysBaseUrl: params.intellsysBaseUrl,
        workspaceId: params.workspaceId,
        eventId: firstLead.id,
        templateId: leadTemplateId,
        data: mergedAdditional as never,
    });
}
